import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Session, RunnerType } from '@claude-code-api/shared';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { getGiftAgentWorkspaceUrl, getSessionOpenUrl, isPmCapableRunner } from '@/lib/gift-agent';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { Cpu, HardDrive } from 'lucide-react';

interface SessionResourceConfig {
  projectId: string;
  cpuCores: number;
  memoryGb: number;
  runnerType: RunnerType;
}

const RESOURCE_PRESETS: { cpuCores: number; memoryGb: number }[] = [
  { cpuCores: 2, memoryGb: 4 },
  { cpuCores: 3, memoryGb: 6 },
  { cpuCores: 4, memoryGb: 8 },
  { cpuCores: 6, memoryGb: 12 },
  { cpuCores: 8, memoryGb: 16 },
];

const RUNNER_OPTION_KEYS: Record<
  RunnerType,
  { label: string; description: string }
> = {
  'claude-code': {
    label: 'startSessionModal.runners.claudeCode.label',
    description: 'startSessionModal.runners.claudeCode.description',
  },
  acpx: {
    label: 'startSessionModal.runners.acpx.label',
    description: 'startSessionModal.runners.acpx.description',
  },
  'acpx-experimental': {
    label: 'startSessionModal.runners.acpxExperimental.label',
    description: 'startSessionModal.runners.acpxExperimental.description',
  },
  'gift-ui': {
    label: 'startSessionModal.runners.giftUi.label',
    description: 'startSessionModal.runners.giftUi.description',
  },
};

const ADMIN_RUNNER_OPTIONS: RunnerType[] = ['claude-code', 'acpx', 'acpx-experimental', 'gift-ui'];
const DEFAULT_USER_RUNNER: RunnerType = 'acpx-experimental';

interface StartSessionModalProps {
  projectId: string | null;
  onClose: () => void;
  onSessionCreated?: (session: Session) => void;
  navigateToSession?: boolean;
}

export function StartSessionModal({
  projectId,
  onClose,
  onSessionCreated,
  navigateToSession = true,
}: StartSessionModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common']);
  const { isAdmin } = useAuth();

  const [poolLimits, setPoolLimits] = useState<{ cpuCores: number; memoryGb: number } | null>(null);
  const [poolAvailable, setPoolAvailable] = useState<{ cpuCores: number; memoryGb: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [selectedRunner, setSelectedRunner] = useState<RunnerType>(isAdmin ? 'claude-code' : DEFAULT_USER_RUNNER);
  const [resourceConfig, setResourceConfig] = useState<SessionResourceConfig | null>(null);
  const availableRunnerOptions = useMemo(
    () => (isAdmin ? ADMIN_RUNNER_OPTIONS : [DEFAULT_USER_RUNNER]),
    [isAdmin],
  );

  // Fetch user data when modal opens
  useEffect(() => {
    if (projectId) {
      void fetchPool();
    }
  }, [projectId]);

  useEffect(() => {
    const defaultRunner = isAdmin ? 'claude-code' : DEFAULT_USER_RUNNER;
    if (!availableRunnerOptions.includes(selectedRunner)) {
      setSelectedRunner(defaultRunner);
      setResourceConfig((current) => (current ? { ...current, runnerType: defaultRunner } : current));
    }
  }, [availableRunnerOptions, isAdmin, selectedRunner]);

  // Initialize resource config when data is loaded
  useEffect(() => {
    if (poolLimits && poolAvailable && projectId) {
      const selectablePresets = RESOURCE_PRESETS.filter((preset) => {
        const withinLimits = preset.cpuCores <= poolLimits.cpuCores && preset.memoryGb <= poolLimits.memoryGb;
        const withinAvailable = preset.cpuCores <= poolAvailable.cpuCores && preset.memoryGb <= poolAvailable.memoryGb;
        return withinLimits && withinAvailable;
      });
      const defaultPreset = selectablePresets.find((preset) => preset.cpuCores === 2 && preset.memoryGb === 4)
        || selectablePresets[0];
      if (!defaultPreset) {
        setResourceConfig(null);
        return;
      }
      setResourceConfig({
        projectId,
        cpuCores: defaultPreset.cpuCores,
        memoryGb: defaultPreset.memoryGb,
        runnerType: selectedRunner,
      });
    }
  }, [poolLimits, poolAvailable, projectId, selectedRunner]);

  const fetchPool = async () => {
    setLoading(true);
    try {
      const subRes = await fetch('/api/subscriptions/me', { credentials: 'include' });
      if (subRes.ok) {
        const data = await subRes.json();
        const pool = data?.data?.pool;
        if (pool?.effectiveLimits) {
          setPoolLimits({
            cpuCores: Number(pool.effectiveLimits.cpuCores) || 0,
            memoryGb: Number(pool.effectiveLimits.memoryGb) || 0,
          });
        }
        if (pool?.available) {
          setPoolAvailable({
            cpuCores: Number(pool.available.cpuCores) || 0,
            memoryGb: Number(pool.available.memoryGb) || 0,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch resource pool:', error);
      toast.error(t('startSessionModal.loadResourceInfoFailed'));
    } finally {
      setLoading(false);
    }
  };

  const startSession = async () => {
    if (!resourceConfig) return;

    setStarting(true);

    try {
      const response = await fetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          projectId: resourceConfig.projectId,
          cpuCores: resourceConfig.cpuCores,
          memoryGb: resourceConfig.memoryGb,
          runnerType: resourceConfig.runnerType,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const createdSession = data.data as Session;
        onSessionCreated?.(createdSession);
        onClose();
        if (navigateToSession) {
          const targetUrl = isPmCapableRunner(createdSession.runnerType)
            ? getGiftAgentWorkspaceUrl(createdSession.id)
            : getSessionOpenUrl(createdSession.id);
          window.open(targetUrl, '_blank', 'noopener,noreferrer');
          navigate(`/sessions/${createdSession.id}`);
        }
      } else {
        toast.error(data.error || t('startSessionModal.startSessionFailed'));
      }
    } catch (err) {
      console.error('Failed to start session:', err);
      toast.error(t('startSessionModal.startSessionFailed'));
    } finally {
      setStarting(false);
    }
  };

  const handleClose = () => {
    if (!starting) {
      onClose();
    }
  };

  const isOpen = projectId !== null;
  const canStartSession = Boolean(resourceConfig);

  const updateRunnerType = (runnerType: RunnerType) => {
    setSelectedRunner(runnerType);
    setResourceConfig((current) => (current ? { ...current, runnerType } : current));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('startSessionModal.title')}</DialogTitle>
          <DialogDescription>
            {t('startSessionModal.description')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner className="h-8 w-8" />
          </div>
        ) : poolLimits ? (
          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <Label>{t('startSessionModal.codeRunner')}</Label>
              <div className="grid grid-cols-2 gap-3">
                {availableRunnerOptions.map((runnerType) => {
                  const option = RUNNER_OPTION_KEYS[runnerType];
                  const selected = selectedRunner === runnerType;

                  return (
                    <button
                      key={runnerType}
                      type="button"
                      onClick={() => updateRunnerType(runnerType)}
                      className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                        selected
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                    >
                      <div className={`text-sm font-bold mb-1 ${selected ? 'text-primary' : 'text-foreground'}`}>
                        {t(option.label)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t(option.description)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <Label>{t('startSessionModal.resourceTier')}</Label>
                {resourceConfig ? (
                  <div className="flex items-center gap-3">
                    <span className="flex items-center text-primary">
                      <Cpu className="w-4 h-4 mr-1" />
                      <span className="font-bold">{resourceConfig.cpuCores} CPU</span>
                    </span>
                    <span className="text-muted-foreground">:</span>
                    <span className="flex items-center text-warning">
                      <HardDrive className="w-4 h-4 mr-1" />
                      <span className="font-bold">{resourceConfig.memoryGb} GB</span>
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-5 gap-2">
              {RESOURCE_PRESETS.map((preset) => {
                const exceedsLimit = preset.cpuCores > (poolLimits?.cpuCores ?? 0)
                  || preset.memoryGb > (poolLimits?.memoryGb ?? 0);
                const exceedsAvailable = poolAvailable
                  ? preset.cpuCores > poolAvailable.cpuCores
                    || preset.memoryGb > poolAvailable.memoryGb
                  : false;
                const disabled = exceedsLimit || exceedsAvailable;
                const isSelected =
                  resourceConfig?.cpuCores === preset.cpuCores
                  && resourceConfig?.memoryGb === preset.memoryGb;
                return (
                  <button
                    key={`${preset.cpuCores}:${preset.memoryGb}`}
                    type="button"
                    disabled={disabled}
                    onClick={() => setResourceConfig({
                      projectId: projectId as string,
                      cpuCores: preset.cpuCores,
                      memoryGb: preset.memoryGb,
                      runnerType: selectedRunner,
                    })}
                    className={`p-2 rounded-xl border-2 text-center transition-all ${
                      disabled
                        ? 'border-border opacity-40 cursor-not-allowed'
                        : isSelected
                          ? 'border-primary bg-primary/10 cursor-pointer'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50 cursor-pointer'
                    }`}
                  >
                    <div className={`text-sm font-bold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                      {preset.cpuCores}:{preset.memoryGb}
                    </div>
                    <div className="text-xs text-muted-foreground">{t('startSessionModal.cpuMemoryShort')}</div>
                  </button>
                );
              })}
            </div>

            <div className="bg-muted/50 rounded-xl p-4 border">
              {resourceConfig ? (
                <p className="text-sm text-center">
                  {t('startSessionModal.summaryPrefix')} <span className="font-semibold text-primary">{t('startSessionModal.cpuSummary', { count: resourceConfig.cpuCores, cpuCores: resourceConfig.cpuCores })}</span> {t('startSessionModal.summaryConnector')} <span className="font-semibold text-warning">{t('startSessionModal.ramSummary', { memoryGb: resourceConfig.memoryGb })}</span>
                </p>
              ) : (
                <p className="text-sm text-center text-muted-foreground">
                  {t('startSessionModal.noTierAvailable')}
                </p>
              )}
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={starting}
          >
            {t('common:cancel')}
          </Button>
          <Button onClick={startSession} disabled={starting || loading || !canStartSession}>
            {starting ? (
              <>
                <Spinner className="w-4 h-4 mr-2" />
                {t('startSessionModal.starting')}
              </>
            ) : (
              t('startSessionModal.startSession')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
